<template>
  <div class="container">
    <div v-if="loading" class="loading-container">
      <i class="fa fa-spinner fa-spin"></i>
      <span>Loading trust details...</span>
    </div>

    <div v-else-if="!trust" class="loading-container">
      <span>Trust details could not be loaded.</span>
      <button class="btn-back" type="button" @click="goBack">Back</button>
    </div>

    <div v-else>
      <!-- Trust header -->
      <div class="promo-section">
        <div class="promo-section-header">
          <h4 class="promo-section-title">Trust Information</h4>
          <span class="extra-data">Trust No: {{ trust.id }}</span>
        </div>

        <div class="promo-details-box full-width name-status">
          <label>Trust Status</label>
          <div class="name-status-row">
            <p>
              Dealer: {{ show(trust.dealer_name) }}
              (ID: {{ show(trust.dealer_id) }})
            </p>
            <span class="status-badge" :class="statusInfo.class">
              <span class="status-dot"></span>
              {{ statusInfo.text }}
            </span>
          </div>
        </div>

        <div class="promo-details-row">
          <div class="promo-details-box">
            <label>Submitted By</label>
            <p>{{ show(trust.created_by_name) }}</p>
          </div>

          <div class="promo-details-box">
            <label>Submitted Date &amp; Time</label>
            <p>{{ formatDateTime(trust.created_at) }}</p>
          </div>
        </div>

        <div class="promo-details-box full-width">
          <label>Remarks</label>
          <p>{{ show(trust.remarks) }}</p>
        </div>
      </div>

      <!-- Trust items -->
      <div class="promo-section">
        <div class="promo-section-header">
          <h4 class="promo-section-title">Trust Items</h4>
          <span class="extra-data">No of Items: {{ items.length }}</span>
        </div>

        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Quantity</th>
                <th>From Date</th>
                <th>To Date</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(item, index) in items" :key="index">
                <td>{{ show(item.item_name) }}</td>
                <td>{{ show(item.qty) }}</td>
                <td>{{ formatDate(item.from_date) }}</td>
                <td>{{ formatDate(item.to_date) }}</td>
              </tr>

              <tr v-if="items.length === 0">
                <td colspan="4" class="muted center">
                  No trust items found.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Uploaded documents -->
      <div class="promo-section">
        <div class="promo-section-header">
          <h4 class="promo-section-title">Documents</h4>
          <span class="extra-data">No of Documents: {{ documents.length }}</span>
        </div>

        <div class="doc-grid">
          <div
            v-for="(doc, index) in documents"
            :key="doc.trust_document_id || index"
            class="doc-card"
          >
            <div class="doc-icon">
              <i :class="fileIcon(doc)"></i>
              <small>{{ fileExt(doc).toUpperCase() || "FILE" }}</small>
            </div>

            <div class="doc-label">
              {{ show(doc.document_name) }}
            </div>
            <div class="doc-file-name muted">
              {{ show(doc.document_file_name) }}
            </div>

            <a
              v-if="documentUrl(doc)"
              :href="documentUrl(doc)"
              target="_blank"
              rel="noopener noreferrer"
              class="document-link"
            >
              Open document
            </a>
          </div>

          <p v-if="documents.length === 0" class="muted">
            No documents uploaded.
          </p>
        </div>
      </div>

      <!-- Actions -->
      <div class="form-actions">
        <button type="button" class="btn-back" @click="goBack">
          Back
        </button>

        <template v-if="canReview">
          <button
            type="button"
            class="btn-reject"
            :disabled="submitting"
            @click="openConfirm('reject')"
          >
            Reject
          </button>

          <button
            type="button"
            class="btn-approve"
            :disabled="submitting"
            @click="openConfirm('approve')"
          >
            Approve
          </button>
        </template>
      </div>
    </div>

    <!-- Confirmation modal -->
    <div v-if="showConfirm" class="modal-overlay">
      <div class="confirm-modal">
        <div class="confirm-icon" :class="currentAction">
          <i
            :class="currentAction === 'approve'
              ? 'fa fa-check-circle'
              : 'fa fa-exclamation-triangle'"
          ></i>
        </div>

        <h4>
          {{ currentAction === 'approve' ? 'Approve Trust' : 'Reject Trust' }}
        </h4>

        <p>
          Are you sure you want to
          <strong>{{ currentAction }}</strong>
          Trust #{{ trust.id }}?
        </p>

        <div class="confirm-actions">
          <button
            type="button"
            class="btn-back"
            :disabled="submitting"
            @click="closeConfirm"
          >
            Cancel
          </button>

          <button
            type="button"
            :class="currentAction === 'approve' ? 'btn-approve' : 'btn-reject'"
            :disabled="submitting"
            @click="submitReview"
          >
            <i v-if="submitting" class="fa fa-spinner fa-spin"></i>
            {{
              submitting
                ? 'Saving...'
                : currentAction === 'approve' ? 'Approve' : 'Reject'
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import {
  fetchTrustDetailsById,
  updateTrustStatus,
} from "@/services/dataService";

export default {
  name: "ReviewTrust",

  setup() {
    const toast = useToast();
    return { toast };
  },

  data() {
    return {
      token: localStorage.getItem("token_set"),
      loading: false,
      submitting: false,
      trust: null,
      items: [],
      documents: [],
      showConfirm: false,
      currentAction: null,
    };
  },

  computed: {
    trustId() {
      return this.$route.params.id;
    },

    // 0 = Pending, 1 = Approved, 2 = Rejected.
    // Uses header.approval_status if the backend sends it, otherwise falls
    // back to ?status= in the route query (if the list page passes it).
    approvalStatus() {
      const fromHeader = this.trust?.approval_status;
      if (fromHeader !== undefined && fromHeader !== null && fromHeader !== "") {
        return Number(fromHeader);
      }

      const fromRoute = this.$route.query.status;
      if (fromRoute !== undefined && fromRoute !== null && fromRoute !== "") {
        return Number(fromRoute);
      }

      return null;
    },

    statusInfo() {
      const statuses = {
        0: { text: "Pending", class: "pending" },
        1: { text: "Approved", class: "approved" },
        2: { text: "Rejected", class: "rejected" },
      };

      return (
        statuses[this.approvalStatus] || {
          text: "Unknown",
          class: "unknown",
        }
      );
    },

    canReview() {
      return this.approvalStatus === null || this.approvalStatus === 0;
    },
  },

  mounted() {
    this.loadTrust();
  },

  methods: {
    async loadTrust() {
      try {
        this.loading = true;
        this.trust = null;

        const response = await fetchTrustDetailsById(
          this.trustId,
          this.token
        );

        if (response.data.status === "success") {
          const { header, items, documents } = response.data.data || {};

          this.trust = header || null;
          this.items = items || [];
          this.documents = documents || [];
        } else {
          this.toast.error(
            response.data.message || "Failed to load trust details."
          );
        }
      } catch (error) {
        console.error("Failed to load trust details.", error);
        this.toast.error(
          error.response?.data?.message || "Failed to load trust details."
        );
      } finally {
        this.loading = false;
      }
    },

    openConfirm(action) {
      this.currentAction = action;
      this.showConfirm = true;
    },

    closeConfirm() {
      if (this.submitting) return;

      this.showConfirm = false;
      this.currentAction = null;
    },

    async submitReview() {
      if (!this.trust || !this.currentAction) return;

      try {
        this.submitting = true;

        const approvalStatus = this.currentAction === "approve" ? 1 : 2;

        const payload = {
            approval_status: approvalStatus
        }

        const response = await updateTrustStatus(
          this.trust.id,
          this.token,
          payload,
        );

        if (response.data.status === "success") {
          this.toast.success(
            response.data.message || "Trust review saved successfully."
          );

          this.showConfirm = false;
          this.goBack();
        } else {
          this.toast.error(
            response.data.message || "Failed to save trust review."
          );
        }
      } catch (error) {
        console.error("Failed to save trust review.", error);

        this.toast.error(
          error.response?.data?.message || "Failed to save trust review."
        );

        if ([404, 409].includes(error.response?.status)) {
          this.showConfirm = false;
          this.currentAction = null;
          await this.loadTrust();
        }
      } finally {
        this.submitting = false;
      }
    },

    documentUrl(doc) {
      return doc.document_file_url || null;
    },

    fileExt(doc) {
      const name = doc.document_file_name || "";
      return name.includes(".") ? name.split(".").pop().toLowerCase() : "";
    },

    fileIcon(doc) {
      const ext = this.fileExt(doc);

      if (ext === "pdf") return "fas fa-file-pdf";
      if (["xls", "xlsx", "csv"].includes(ext)) return "fas fa-file-excel";
      if (["doc", "docx"].includes(ext)) return "fas fa-file-word";
      if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) {
        return "fas fa-file-image";
      }

      return "fas fa-file";
    },

    show(value) {
      return value === null || value === undefined || value === ""
        ? "—"
        : value;
    },

    formatDate(date) {
      if (!date) return "—";

      const d = new Date(date);

      return isNaN(d.getTime()) ? "Invalid Date" : d.toLocaleDateString("en-CA");
    },

    formatDateTime(dateTime) {
      if (!dateTime) return "N/A";

      const date = new Date(String(dateTime).replace(" ", "T"));

      if (isNaN(date.getTime())) return "Invalid Date";

      const formattedDate = date.toLocaleDateString("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });

      const formattedTime = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });

      return `${formattedDate} | ${formattedTime}`;
    },

    goBack() {
      this.$router.push({
        path: "/approval-console",
        query: { tab: "trust-approval" },
      });
    },
  },
};
</script>

<style scoped>
.container {
  background-color: #fff;
  padding: 20px 15px;
  margin: 10px auto;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-sizing: border-box;
}

.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 250px;
  color: #007bff;
  gap: 10px;
}

.promo-section {
  margin-bottom: 30px;
}

.promo-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.promo-section-title {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #333;
}

.extra-data {
  font-size: 13px;
  color: grey;
}

.promo-details-row {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.promo-details-box {
  flex: 1;
  min-width: 0;
  background-color: #f6f6f6;
  border-radius: 6px;
  padding: 8px 12px;
  border: 1px solid #ddd;
}

.promo-details-box.full-width {
  margin-bottom: 10px;
}

.promo-details-box label {
  display: block;
  font-weight: bold;
  font-size: 12px;
  margin-bottom: 4px;
  color: #333;
}

.promo-details-box p {
  font-size: 14px;
  margin: 0;
  color: #000;
  word-break: break-word;
  white-space: normal;
}

.name-status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.status-badge {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: bold;
  padding: 4px 8px;
  border-radius: 12px;
  gap: 6px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.status-badge.pending { color: #8a6d3b; background: #fff3cd; }
.status-badge.pending .status-dot { background: #ffb300; }
.status-badge.approved { color: #155724; background: #d4edda; }
.status-badge.approved .status-dot { background: #28a745; }
.status-badge.rejected { color: #721c24; background: #f8d7da; }
.status-badge.rejected .status-dot { background: #dc3545; }
.status-badge.unknown { color: #333; background: #ddd; }
.status-badge.unknown .status-dot { background: #777; }

.table-wrap {
  width: 100%;
  max-height: 350px;
  overflow: auto;
  border: 1px solid #eee;
  border-radius: 6px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th {
  text-align: left;
  border-bottom: 2px solid #ddd;
  background: #f4f4f4;
  padding: 10px;
  white-space: nowrap;
  position: sticky;
  top: 0;
}

td {
  padding: 10px;
  border-bottom: 1px solid #eee;
}

.muted { color: #9ca3af; }
.center { text-align: center; }

.doc-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.doc-card {
  width: 200px;
  text-align: center;
  overflow-wrap: anywhere;
}

.doc-icon {
  height: 110px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #f3f4f6;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.doc-icon i {
  font-size: 2.5rem;
}

.doc-label {
  margin-top: 6px;
  font-size: 13px;
  font-weight: 500;
}

.doc-file-name {
  font-size: 12px;
}

.document-link {
  display: inline-block;
  margin-top: 5px;
  font-size: 13px;
}

.form-actions {
  margin-top: 30px;
  padding-top: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-back,
.btn-reject,
.btn-approve {
  padding: 8px 18px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  color: #fff;
  min-width: 100px;
}

.btn-back { background: #6c757d; }
.btn-back:hover { background: #545b62; }
.btn-reject { background: #ef4444; }
.btn-reject:hover { background: #dc2626; }
.btn-approve { background: #22c55e; }
.btn-approve:hover { background: #16a34a; }

.btn-back:disabled,
.btn-reject:disabled,
.btn-approve:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.confirm-modal {
  background: #fff;
  width: 440px;
  max-width: 90%;
  border-radius: 10px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 10px 35px rgba(0, 0, 0, 0.2);
}

.confirm-icon {
  width: 55px;
  height: 55px;
  margin: 0 auto 15px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
}

.confirm-icon.approve { background: #e8f7ed; color: #069e27; }
.confirm-icon.reject { background: #fdecec; color: #dc2626; }

.confirm-modal h4 {
  margin-bottom: 12px;
  font-size: 20px;
}

.confirm-modal p {
  margin-bottom: 15px;
  color: #6c757d;
  font-size: 14px;
}

.confirm-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 20px;
}

@media (max-width: 768px) {
  .promo-details-row {
    flex-direction: column;
  }

  .doc-card {
    width: 100%;
  }
}
</style>